/**
 * Best-effort sample of a live MediaStreamTrack for solid black / green-garbage
 * placeholders. Chromium unmute alone is insufficient — DirectShow can deliver
 * black/green RGB32 placeholders.
 *
 * Admin Connect treats the result as advisory: wait briefly for a non-solid
 * frame, then answer anyway if the track is live/unmuted/sized.
 */

const GREEN_DOMINANCE_THRESHOLD = 0.65;
const MIN_LUMA_VARIANCE = 80;
const SAMPLE_INTERVAL_MS = 100;
const DEFAULT_TIMEOUT_MS = 3_000;
const SAMPLE_WIDTH = 64;
const SAMPLE_HEIGHT = 36;
/** Sparse sample stride — full 64×36 walk is unnecessary for this heuristic. */
const SAMPLE_STRIDE = 4;

export type NonSolidFrameWaitResult = {
  ok: boolean;
  reason: 'ready' | 'timeout' | 'no_track' | 'sample_failed';
};

function isGreenGarbageFrame(imageData: ImageData): boolean {
  const { data, width, height } = imageData;
  let greenDominant = 0;
  let count = 0;
  for (let y = 0; y < height; y += SAMPLE_STRIDE) {
    for (let x = 0; x < width; x += SAMPLE_STRIDE) {
      const i = (y * width + x) * 4;
      // Canvas getImageData is RGBA.
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (g > 160 && g > r + 35 && g > b + 35) {
        greenDominant += 1;
      }
      count += 1;
    }
  }
  return count > 0 && greenDominant / count >= GREEN_DOMINANCE_THRESHOLD;
}

function isNearSolidFrame(imageData: ImageData): boolean {
  const { data, width, height } = imageData;
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 0; y < height; y += SAMPLE_STRIDE) {
    for (let x = 0; x < width; x += SAMPLE_STRIDE) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      sum += luma;
      sumSq += luma * luma;
      count += 1;
    }
  }
  if (count === 0) return true;
  const mean = sum / count;
  const variance = sumSq / count - mean * mean;
  return variance < MIN_LUMA_VARIANCE;
}

export function isNonSolidVideoFrame(imageData: ImageData): boolean {
  return !isGreenGarbageFrame(imageData) && !isNearSolidFrame(imageData);
}

function attachProbeVideo(track: MediaStreamTrack): HTMLVideoElement | null {
  if (typeof document === 'undefined') return null;
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([track]);
  void video.play().catch(() => undefined);
  return video;
}

function sampleProbeFrame(video: HTMLVideoElement): ImageData | null {
  if (video.videoWidth === 0 || video.videoHeight === 0) return null;
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_WIDTH;
  canvas.height = SAMPLE_HEIGHT;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
  return ctx.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
}

function detachProbeVideo(video: HTMLVideoElement | null): void {
  if (!video) return;
  video.pause();
  video.srcObject = null;
}

/**
 * Poll until a non-solid frame is observed or timeout.
 * Callers should treat timeout as advisory (still answer if track is otherwise ready).
 */
export async function waitForNonSolidVideoFrame(
  track: MediaStreamTrack | null,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<NonSolidFrameWaitResult> {
  if (!track || track.readyState !== 'live') {
    return { ok: false, reason: 'no_track' };
  }

  const video = attachProbeVideo(track);
  if (!video) {
    return { ok: false, reason: 'sample_failed' };
  }

  const deadline = Date.now() + timeoutMs;
  let sampled = false;
  try {
    while (Date.now() < deadline) {
      if (track.readyState !== 'live') {
        return { ok: false, reason: 'no_track' };
      }
      if (!track.muted) {
        const frame = sampleProbeFrame(video);
        if (frame) {
          sampled = true;
          if (isNonSolidVideoFrame(frame)) {
            return { ok: true, reason: 'ready' };
          }
        }
      }
      await new Promise<void>((resolve) => setTimeout(resolve, SAMPLE_INTERVAL_MS));
    }
  } finally {
    detachProbeVideo(video);
  }

  return { ok: false, reason: sampled ? 'timeout' : 'sample_failed' };
}
